import dayjs from 'dayjs';
import type { MeUser } from '~/server/types';
import { isImageFile } from '~/modules/file/config/image-upload';
import type {
    Conversation,
    Message,
    Participant,
    ReplyToMessage,
    TextOrFileMessage,
} from '../types';

// Use the least restrictive type for the arguments.
type PartialConversation = Pick<Conversation, '__typename' | 'Participants'>;

/**
 * Create a conversation message object for optimistic UI.
 * @returns A conversation message object.
 */
export function createOptimisticMessage({
    conversationID,
    message,
    attachments,
    replyTo,
    conversation,
    user,
}: {
    conversationID: number;
    message?: string;
    attachments?: File[];
    replyTo?: TextOrFileMessage | null;
    conversation?: PartialConversation | null;
    user: MeUser;
}): Message {
    const now = dayjs().utc();
    const selfParticipant = getSelfParticipant(conversation, user);
    let replyToMessage: ReplyToMessage | null = replyTo ?? null;
    // If replying to a message that is itself a reply and has no text or files,
    // use the original message being replied to as the reply to message.
    if (
        replyTo &&
        !replyTo.Content &&
        (replyTo.__typename !== 'FileAttachmentMessage' ||
            !replyTo.Attachments?.length) &&
        replyTo.ReplyTo
    ) {
        replyToMessage = replyTo.ReplyTo;
    }

    if (attachments?.length) {
        return {
            isOptimistic: true,
            __typename: 'FileAttachmentMessage',
            ID: crypto.randomUUID(),
            ConversationID: conversationID,
            Content: message ?? '',
            IsHTML: false,
            Attachments: attachments.map((attachment, index) => ({
                __typename: 'MemberUpload',
                ID: now.unix() + index,
                // Use a blob URL for images..
                Link: isImageFile(attachment.name)
                    ? URL.createObjectURL(attachment)
                    : '',
                FileName: attachment.name,
            })),
            ReplyTo: replyToMessage,
            Participant: selfParticipant,
            Created: now.format('YYYY-MM-DD HH:mm:ss'),
            CreatedBy: user.Name,
        };
    }
    return {
        isOptimistic: true,
        __typename: 'TextMessage',
        ID: crypto.randomUUID(),
        ConversationID: conversationID,
        Content: message ?? '',
        IsHTML: false,
        ReplyTo: replyToMessage,
        Participant: selfParticipant,
        Created: now.format('YYYY-MM-DD HH:mm:ss'),
        CreatedBy: user.Name,
    };
}

/**
 * Get the participant object for the current user from the conversation. If the
 * participant is not found, return a mock participant object for the current user.
 * @param conversation The conversation to get the participant from.
 * @param user The current user.
 * @returns The participant object for the current user.
 */
function getSelfParticipant(
    conversation: PartialConversation | null | undefined,
    user: MeUser
): Participant {
    // The typecheck will fail if we don't cast this type for some reason.
    const participants = (conversation?.Participants ?? []) as Participant[];
    const selfParticipant = participants.find(
        ({ Identity }) =>
            Identity.__typename === 'Member' && Identity.ID === user.ID
    );
    if (selfParticipant) {
        return selfParticipant;
    }
    // `selfParticipant` should always exist, but just in case.
    return mockSelfParticipant(user, conversation);
}

/**
 * Mock the participant object for the current user.
 * @param user The current user.
 * @param conversation The conversation generate the mock participant for.
 * @returns The participant object for the current user.
 */
function mockSelfParticipant(
    user: MeUser,
    conversation: PartialConversation | null | undefined
): Participant {
    const identity = {
        __typename: 'Member',
        ID: user.ID,
        Name: user.Name,
        FirstName: user.FirstName,
        LastName: user.LastName,
        Initials: user.Initials,
        Phone: user.Phone,
        ProfileImageLink: user.ProfileImage,
        UserType: user.UserType,
        ProfessionalCategory: user.ProfessionalCategory,
        IsBlocked: false,
    } satisfies Participant['Identity'];
    if (conversation?.__typename === 'EnquiryConversation') {
        return {
            __typename: 'EnquiryParticipant',
            ID: crypto.randomUUID(),
            Identity: identity,
            IsCollaborator: conversation.Participants.some(
                (p) =>
                    p.__typename === 'EnquiryParticipant' &&
                    p.Identity.__typename === 'Member' &&
                    p.Identity.ID === user.ID &&
                    p.IsCollaborator
            ),
        };
    }
    return {
        __typename: 'MemberParticipant',
        ID: crypto.randomUUID(),
        Identity: identity,
    };
}

/**
 * Revoke blob URLs created for optimistic image attachments to free memory.
 * Call this when optimistic messages are replaced by server responses or cleared.
 * @param messages The optimistic messages to clean up.
 */
export function revokeOptimisticBlobUrls(messages: Message[]): void {
    for (const message of messages) {
        if (
            message.isOptimistic &&
            message.__typename === 'FileAttachmentMessage'
        ) {
            for (const attachment of message.Attachments) {
                if (attachment.Link.startsWith('blob:')) {
                    URL.revokeObjectURL(attachment.Link);
                }
            }
        }
    }
}

/**
 * Convert a message to a deleted message.
 * @param message The message to convert.
 * @returns The deleted message with the same ID and other fields.
 */
export function convertToDeletedMessage(
    message: Message
): Extract<Message, { __typename: 'DeletedMessage' }> {
    return {
        ...message,
        __typename: 'DeletedMessage',
    };
}
